import { Prose } from '../../components/Prose'
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, type UserRow } from '../../api/client'
import { trDate } from '../../lib/format'
import { UsagePanel } from './UsagePanel'

/**
 * Yönetici bölümü: hesaplar + kullanım özeti.
 *
 * İkisi ayrı sekmede çünkü ayrı işler. "Parola sıfırla" günde bir kez lazım
 * olan bir şey; "kim ne kadar kullanıyor" ise sık bakılan taraf — ama
 * kullanım tablosunu sil/parola düğmelerinin arasına koymak yanlış tıklama
 * daveti olurdu.
 */
export function AdminTab() {
  const [gorunum, setGorunum] = useState<'kullanim' | 'hesaplar'>('kullanim')

  return (
    <>
      <div className="segbar" role="tablist" aria-label="Yönetici görünümü">
        <button role="tab" aria-selected={gorunum === 'kullanim'}
                onClick={() => setGorunum('kullanim')}>Kullanım</button>
        <button role="tab" aria-selected={gorunum === 'hesaplar'}
                onClick={() => setGorunum('hesaplar')}>Hesaplar</button>
      </div>
      {gorunum === 'kullanim' ? <UsagePanel /> : <Hesaplar />}
    </>
  )
}

/**
 * Kullanıcı yönetimi. Kayıt formu yok — hesapları buradan sen açıyorsun.
 * Parola oluşturulduğu anda bir kez gösteriliyor, hiçbir yerde saklanmıyor.
 */
function Hesaplar() {
  const qc = useQueryClient()
  const users = useQuery({ queryKey: ['admin-users'], queryFn: api.listUsers })

  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [isAdmin, setIsAdmin] = useState(false)
  const [pass, setPass] = useState('')
  // Satır bazlı parola belirleme — hangi kullanıcının kutusu açık.
  const [pwFor, setPwFor] = useState<number | null>(null)
  const [pwValue, setPwValue] = useState('')
  const [shown, setShown] = useState<{ email: string; password: string } | null>(null)
  const [error, setError] = useState<string | null>(null)

  const create = useMutation({
    mutationFn: () => api.createUser(email.trim(), name.trim(), isAdmin, pass.trim() || null),
    onSuccess: (r) => {
      // Parolayı sen yazdıysan ekranda tekrar göstermenin anlamı yok; sadece
      // üretilmiş parolayı gösteriyoruz (bir daha görme şansın yok).
      if (!pass.trim()) setShown({ email: r.email, password: r.password })
      setEmail(''); setName(''); setIsAdmin(false); setPass(''); setError(null)
      qc.invalidateQueries({ queryKey: ['admin-users'] })
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Oluşturulamadı'),
  })

  const reset = useMutation({
    mutationFn: (v: { id: number; password: string | null }) => api.resetPassword(v.id, v.password),
    onSuccess: (r, v) => {
      const u = users.data?.find((x) => x.id === v.id)
      if (!v.password) setShown({ email: u?.email ?? `#${v.id}`, password: r.password })
      setPwFor(null); setPwValue(''); setError(null)
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Parola değiştirilemedi'),
  })

  const remove = useMutation({
    mutationFn: (id: number) => api.deleteUser(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-users'] }),
    onError: (e) => setError(e instanceof Error ? e.message : 'Silinemedi'),
  })

  if (users.error) {
    return (
      <div className="card">
        <h2>Yetkin yok</h2>
        <p className="sub">Bu bölüm sadece yönetici hesapları için.</p>
      </div>
    )
  }

  return (
    <>
      {shown && (
        <div className="card card-enter" style={{ borderLeft: '3px solid var(--warn)' }}>
          <h2>Parola oluşturuldu</h2>
          <Prose className="card-h">
            Bu parola <strong>bir daha gösterilmeyecek</strong> — sunucuda sadece hash'i duruyor.
            Şimdi kopyala ve kullanıcıya ilet.
          </Prose>
          <div className="row">
            <div>
              <label className="f">E-posta</label>
              <input readOnly value={shown.email} onFocus={(e) => e.target.select()} />
            </div>
            <div>
              <label className="f">Parola</label>
              <input readOnly value={shown.password} onFocus={(e) => e.target.select()}
                     style={{ fontFamily: 'ui-monospace, monospace' }} />
            </div>
            <button onClick={() => navigator.clipboard?.writeText(shown.password)}>Kopyala</button>
            <button onClick={() => setShown(null)}>Kapat</button>
          </div>
        </div>
      )}

      <div className="card">
        <h2>Yeni kullanıcı</h2>
        <Prose className="card-h">
          Parolayı boş bırakırsan güçlü bir tane üretilir ve <strong>bir kez</strong> gösterilir —
          önerilen yol bu. Kendin yazarsan en az 8 karakter olmalı.
        </Prose>
        <div className="row">
          <div>
            <label className="f">E-posta</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="ali@ornek.com" />
          </div>
          <div>
            <label className="f">İsim</label>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ali" />
          </div>
          <div>
            <label className="f">Parola (boş = üret)</label>
            <input type="password" autoComplete="new-password" value={pass}
                   onChange={(e) => setPass(e.target.value)} placeholder="en az 8 karakter" />
          </div>
          <button className="primary" disabled={!email.includes('@') || create.isPending}
                  onClick={() => create.mutate()}>
            {create.isPending ? 'Oluşturuluyor…' : 'Oluştur'}
          </button>
        </div>
        <label className="chk" style={{ marginTop: 10 }}>
          <input type="checkbox" checked={isAdmin} onChange={(e) => setIsAdmin(e.target.checked)} />
          <span>
            <span className="t">Yönetici olsun</span><br />
            <span className="d">Kullanıcı açabilir, parola sıfırlayabilir, hesap silebilir</span>
          </span>
        </label>
        {error && <p style={{ color: 'var(--crit)', fontSize: 13.5, marginTop: 10 }}>{error}</p>}
      </div>

      <div className="card">
        <h2>Kullanıcılar</h2>
        <table>
          <thead>
            <tr><th>E-posta</th><th>İsim</th><th>Rol</th><th>Son giriş</th><th /></tr>
          </thead>
          <tbody>
            {users.isLoading && <tr><td colSpan={5} style={{ color: 'var(--muted)' }}>Yükleniyor…</td></tr>}
            {users.data?.map((u: UserRow) => (
              <tr key={u.id}>
                <td style={{ fontVariantNumeric: 'normal' }}>{u.email}</td>
                <td style={{ fontVariantNumeric: 'normal' }}>{u.name}</td>
                <td>{u.isAdmin ? <span className="pill">yönetici</span> : '—'}</td>
                <td>{trDate(u.lastLoginAt.slice(0, 10))}</td>
                <td className="act">
                  <button onClick={() => { setPwFor(pwFor === u.id ? null : u.id); setPwValue('') }}>
                    {pwFor === u.id ? 'vazgeç' : 'parola belirle'}
                  </button>{' '}
                  <button onClick={() => reset.mutate({ id: u.id, password: null })}
                          disabled={reset.isPending}>rastgele üret</button>{' '}
                  <button onClick={() => {
                    if (confirm(`${u.email} ve TÜM verileri silinecek. Emin misin?`)) remove.mutate(u.id)
                  }}>sil</button>
                </td>
              </tr>
            )).flatMap((row, i) => {
              const u = users.data![i]
              if (pwFor !== u.id) return [row]
              return [row, (
                <tr key={`${u.id}-pw`}>
                  <td colSpan={5} style={{ background: 'var(--chip)' }}>
                    <div className="row" style={{ alignItems: 'flex-end' }}>
                      <div style={{ flex: '1 1 200px' }}>
                        <label className="f">{u.email} için yeni parola</label>
                        <input type="password" autoComplete="new-password" value={pwValue}
                               autoFocus placeholder="en az 8 karakter"
                               onChange={(e) => setPwValue(e.target.value)}
                               onKeyDown={(e) => {
                                 if (e.key === 'Enter' && pwValue.length >= 8)
                                   reset.mutate({ id: u.id, password: pwValue })
                               }} />
                      </div>
                      <button className="primary" disabled={pwValue.length < 8 || reset.isPending}
                              onClick={() => reset.mutate({ id: u.id, password: pwValue })}>
                        Uygula
                      </button>
                    </div>
                  </td>
                </tr>
              )]
            })}
          </tbody>
        </table>
        <Prose className="note">
          Kullanıcı silmek onun tartı geçmişini, günlüklerini ve programını da siler — geri alınamaz.
          Parola hiçbir zaman düz metin saklanmıyor; sunucuda PBKDF2 ile hash'i duruyor, o yüzden
          "mevcut parola neydi" sorusunun cevabı yok — sadece yenisini koyabilirsin.
        </Prose>
      </div>
    </>
  )
}
